/**
 * `/api/claims` wrappers — reward calculation, claim submission and scheduling.
 *
 * The two `calculate*` reads are pure GETs. The three writes all carry a signed
 * envelope (`signature`/`message`/`nonce`/`nonceToken`) minted by
 * `src/lib/api/auth.ts`; this module does not build it, it only forwards it.
 */
import type {
  CalculateLeasingRewardsQuery,
  CalculateLeasingRewardsResponse,
  CalculateUnlockedRewardsQuery,
  CalculateUnlockedRewardsResponse,
  ClaimDailyRewardsRequest,
  ClaimLeasingRewardsRequest,
  ClaimSubmitResponse,
  GetClaimsByUserAddressQuery,
  GetClaimsByUserAddressResponse,
  ScheduleClaimRequest,
  ScheduleClaimResponse,
} from "@swarm/shared/schemas";
import {
  calculateLeasingRewardsResponseSchema,
  calculateUnlockedRewardsResponseSchema,
  claimSubmitResponseSchema,
  getClaimsByUserAddressResponseSchema,
  scheduleClaimResponseSchema,
} from "@swarm/shared/schemas";

import { routes } from "@/hooks/useRoutes/useRoutes";

import { apiRequest } from "./client";

/**
 * Daily-rewards breakdown for a wallet.
 *
 * The server maintains `lockedRewards + unlockedRewards + totalClaimed ===
 * totalRewards`; the dashboard's "Total Tokens" tile adds the three, so never
 * re-derive any of them client-side.
 */
export const calculateUnlockedRewards = (
  query: CalculateUnlockedRewardsQuery,
): Promise<CalculateUnlockedRewardsResponse> =>
  apiRequest(
    routes.api.claims.calculateUnlockedRewards(query),
    calculateUnlockedRewardsResponseSchema,
  );

/**
 * Per-lease reward breakdown.
 *
 * `isClaimingEnabled` is the composite of the kill switch, the TGE date, lease
 * maturity and "no claim already queued" — render it, do not recompute it.
 */
export const calculateLeasingRewards = (
  query: CalculateLeasingRewardsQuery,
): Promise<CalculateLeasingRewardsResponse> =>
  apiRequest(
    routes.api.claims.calculateLeasingRewards(query),
    calculateLeasingRewardsResponseSchema,
  );

/** Every claim row for a wallet, newest first. */
export const getClaimsByUserAddress = (
  query: GetClaimsByUserAddressQuery,
): Promise<GetClaimsByUserAddressResponse> =>
  apiRequest(routes.api.claims.getByUserAddress(query), getClaimsByUserAddressResponseSchema);

/** Reserves a daily-rewards payout. The transaction settles asynchronously. */
export const claimDailyRewards = (body: ClaimDailyRewardsRequest): Promise<ClaimSubmitResponse> =>
  apiRequest(routes.api.claims.dailyRewards(), claimSubmitResponseSchema, {
    method: "POST",
    body,
  });

/** Reserves a leasing-rewards payout for one lease. */
export const claimLeasingRewards = (
  body: ClaimLeasingRewardsRequest,
): Promise<ClaimSubmitResponse> =>
  apiRequest(routes.api.claims.leasingRewards(), claimSubmitResponseSchema, {
    method: "POST",
    body,
  });

/** Queues a claim for day 32 (30% burn) or day 94 (no burn) after TGE. */
export const scheduleClaim = (body: ScheduleClaimRequest): Promise<ScheduleClaimResponse> =>
  apiRequest(routes.api.claims.scheduleClaim(), scheduleClaimResponseSchema, {
    method: "POST",
    body,
  });
